// Generated by TaroBase SDK Generator: 2026-04-14T11:42:05.668Z
// Collection: wipComments/$commentId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Public comments on WIP art posts. Anonymous-friendly, anyone can post. Admin and founder can delete for moderation.
 */

export interface WipCommentsRequest {
  postId: string;
  displayName: string;
  message: string;
  walletAddress?: string;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface WipCommentsResponse {
  postId: string;
  displayName: string;
  message: string;
  walletAddress?: string;
  createdAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a WipComments operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildWipComments(commentId: string, data?: WipCommentsRequest): DocumentOperation {
  const doc = data ? transformValues(data) : {};
  return { path: `wipComments/${commentId}`, document: doc };
}

/**
 * Anyone can post a comment with no authentication required. displayName and message are required. walletAddress is optional (set if the user has a connected wallet). postId, displayName, message, and createdAt are immutable after creation. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setWipComments(commentId: string, data?: WipCommentsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data || {});
    const succeeded = await set(`wipComments/${commentId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting WipComments: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read WIP comments. Displayed publicly under each WIP post in the Artist Room.
   (Get Single Item)
 */
export async function getWipComments(commentId: string): Promise<WipCommentsResponse | null> {
  try {
    const result = await get(`wipComments/${commentId}`);
    return result as WipCommentsResponse | null;
  } catch (error) {
    console.error(`Error getting WipComments: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single WipComments document. (
  Read Operation Details: Anyone can read WIP comments. Displayed publicly under each WIP post in the Artist Room.
  )
 */
export function subscribeWipComments(callback: (data: WipCommentsResponse | null) => void, commentId: string): Promise<() => Promise<void>> {
  return subscribe(`wipComments/${commentId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as WipCommentsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to WipComments at path '${`wipComments/${commentId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many WipComments items from collection wipComments
 
  Read Operation Details: Anyone can read WIP comments. Displayed publicly under each WIP post in the Artist Room.
  
 */
export async function getManyWipComments(filter: string = ""): Promise<WipCommentsResponse[]> {
  try {
    const results = await get("wipComments", { prompt: filter });
    return Array.isArray(results) ? results as WipCommentsResponse[] : [];
  } catch (error) {
    console.error(`Error getting WipComments collection at path 'wipComments': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in WipComments collection at wipComments
 
  Read Operation Details: Anyone can read WIP comments. Displayed publicly under each WIP post in the Artist Room.
  
 */
export function subscribeManyWipComments(callback: (data: WipCommentsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("wipComments", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as WipCommentsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to WipComments collection at path '${"wipComments"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all WipComments items from collection wipComments
 
  Read Operation Details: Anyone can read WIP comments. Displayed publicly under each WIP post in the Artist Room.
  
 */
export async function getAllWipComments(filter: string = ""): Promise<WipCommentsResponse[]> {
  try {
    const results = await get("wipComments", { prompt: filter });
    return Array.isArray(results) ? results as WipCommentsResponse[] : [];
  } catch (error) {
    console.error(`Error getting WipComments collection at path 'wipComments': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in WipComments collection at wipComments
 
  Read Operation Details: Anyone can read WIP comments. Displayed publicly under each WIP post in the Artist Room.
  
 */
export function subscribeAllWipComments(callback: (data: WipCommentsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("wipComments", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as WipCommentsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to WipComments collection at path '${"wipComments"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count WipComments items in collection wipComments.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countWipComments(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("wipComments", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting WipComments at path 'wipComments': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on WipComments items in collection wipComments.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateWipComments(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("wipComments", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating WipComments at path 'wipComments': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Admin or founder only. Can remove comments for moderation purposes.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteWipComments(commentId: string): Promise<boolean> {
  try {
    const succeeded = await set(`wipComments/${commentId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting WipComments: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for WipComments for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteWipComments(commentId: string): DocumentOperation {
  return { path: `wipComments/${commentId}`, document: null };
}

